import React, { useEffect, useMemo, useState } from 'react';
import { Container, CircularProgress, Snackbar, Alert, Typography, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useParams, useHistory } from 'react-router-dom';

import { Layout } from 'features/common/components/Layout';
import { BranchOfficeForm, BranchOfficeFormValues } from 'features/branchOffices/components/BranchOfficeForm';

// Types & services (ajustá los paths a tu proyecto)
import { BranchOfficeType, EngineEnum, INetsuiteAccount } from 'features/branchOffices/types';
import {
	getBranchOffice,
	createBranchOffice,
	updateBranchOffice,
	getNetsuiteAccounts,
} from 'features/branchOffices/services';
import { ShowAlertState } from '../../features/common/types';
import { extractErrorMessage } from '../../features/quotation/helpers';
import routes from '../../constants/routes';

// ---------------------------------------------------------------------------------------------------------------------
// CONSTANTES RUTA
// ---------------------------------------------------------------------------------------------------------------------
export const NEW_PHYSICAL = 'new-physical-branch-office';
export const NEW_NOT_PHYSICAL = 'new-not-physical-branch-office';

const useStyles = makeStyles((theme: any) => ({
	paper: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	loadingBox: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '40vh',
	},
}));

function BranchOfficeDetailPage(): JSX.Element {
	const classes = useStyles();
	const history = useHistory();
	const { id: idParam } = useParams<{ id: string }>();

	const isEdit = useMemo(() => !!idParam && !isNaN(Number(idParam)), [idParam]);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);

	const [defaultValues, setDefaultValues] = useState<Partial<BranchOfficeFormValues> | undefined>(undefined);
	const [netsuiteAccounts, setNetsuiteAccounts] = useState<INetsuiteAccount[]>([]);

	// -------------------------------------------------------------
	// Carga inicial: cuentas + (si edita) la branch office
	// -------------------------------------------------------------
	useEffect(() => {
		let mounted = true;

		async function load() {
			try {
				setLoading(true);

				const accountsResult = await getNetsuiteAccounts();
				if (!mounted) return;
				setNetsuiteAccounts(accountsResult.data);

				if (isEdit) {
					const numericId = Number(idParam);
					const entity = await getBranchOffice(numericId);
					if (!mounted) return;
					setDefaultValues(entity.data as any);
				} else {
					if (idParam === NEW_PHYSICAL) {
						setDefaultValues({
							name: '',
							branchOfficeType: BranchOfficeType.PhysicalBranch,
							active: true,
							address: '',
							phoneNumber: '',
							lat: '',
							lng: '',
							email: '',
							image: { id: '', name: '', image: '' },
							schedules: [],
							engines: [] as EngineEnum[],
						} as Partial<BranchOfficeFormValues>);
					} else if (idParam === NEW_NOT_PHYSICAL) {
						setDefaultValues({
							name: '',
							branchOfficeType: BranchOfficeType.OfficeCashDesk,
							active: true,
							onlyAdmins: false,
							currencyConfigs: [],
						} as Partial<BranchOfficeFormValues>);
					} else {
						setDefaultValues({
							name: '',
							branchOfficeType: BranchOfficeType.OfficeCashDesk,
							active: true,
							onlyAdmins: false,
							currencyConfigs: [],
						} as Partial<BranchOfficeFormValues>);
					}
				}
			} catch (e: any) {
				if (!mounted) return;
				setAlert({ show: true, severity: 'error', message: e?.message || 'Error al cargar la información' });
			} finally {
				setLoading(false);
			}
		}

		load();
		return () => {
			mounted = false;
		};
	}, [idParam, isEdit]);

	// -------------------------------------------------------------
	// Submit
	// -------------------------------------------------------------
	const handleSubmitForm = async (values: BranchOfficeFormValues) => {
		try {
			setSaving(true);
			setAlert(defaultAlertState);

			if (isEdit) {
				const numericId = Number(idParam);
				await updateBranchOffice(numericId, values as any);
				setAlert({ show: true, severity: 'success', message: 'Sucursal actualizada correctamente' });
			} else {
				await createBranchOffice(values as any);
				setAlert({ show: true, severity: 'success', message: 'Sucursal creada correctamente' });
			}

			history.push(routes.branchOffices.list);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'No se pudo guardar la sucursal'),
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Layout>
			<>
				<Container component="main" maxWidth="xl">
					<div className={classes.paper}>
						<Typography variant="h5" gutterBottom>
							{isEdit ? 'Editar sucursal' : 'Crear sucursal'}
						</Typography>

						{loading || !defaultValues ? (
							<Box className={classes.loadingBox}>
								<CircularProgress />
							</Box>
						) : (
							<BranchOfficeForm
								defaultValues={defaultValues}
								onSubmit={handleSubmitForm}
								onCancel={() => history.push(routes.branchOffices.list)}
								submitLabel={saving ? 'Guardando…' : 'Guardar'}
								netsuiteAccounts={netsuiteAccounts}
							/>
						)}
					</div>
				</Container>

				<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
					<Alert variant="filled" severity={alert.severity}>
						{alert.message}
					</Alert>
				</Snackbar>
			</>
		</Layout>
	);
}

export default BranchOfficeDetailPage;
